import { useEffect, useMemo, useState, useCallback } from 'react'
import { listTests, deleteTest, requestTestsExport, DATE_PERIOD_OPTIONS } from '../api/testApi'
import { listTestGroups } from '../../testgroups/api/testGroupApi'
import { getMasterOptionItems } from '../../../admission/students/api/mastersApi'
import { appToast } from '../../../../components/AppToast'
import { appConfirm } from '../../../../components/AppConfirmModal'

const DEFAULT_PAGE_SIZE = 25

function generateAcademicYears() {
  const currentYear = new Date().getFullYear()
  const years = []
  for (let y = currentYear - 3; y <= currentYear + 2; y++) {
    years.push(`${y}-${String(y + 1).slice(2)}`)
  }
  return years
}

export function useTestListScreen() {
  const [company, setCompany] = useState('All')
  const [academicYear, setAcademicYear] = useState('All')
  const [branch, setBranch] = useState('All')
  const [course, setCourse] = useState('All')
  const [group, setGroup] = useState('All')
  const [department, setDepartment] = useState('All')
  const [batch, setBatch] = useState('All')
  const [subject, setSubject] = useState('All')
  const [datePeriod, setDatePeriod] = useState('period')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [testNo, setTestNo] = useState('')
  const [searchField, setSearchField] = useState('testName')
  const [searchText, setSearchText] = useState('')

  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [totalCount, setTotalCount] = useState(0)
  const [totalPages, setTotalPages] = useState(1)

  const [items, setItems] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [isMutating, setIsMutating] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [isReportsOpen, setIsReportsOpen] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  // Dynamic Options
  const [companyOptions, setCompanyOptions] = useState([{ value: 'All', label: 'All Companies' }])
  const [branchOptions, setBranchOptions] = useState([{ value: 'All', label: 'All Branches' }])
  const [courseOptions, setCourseOptions] = useState([{ value: 'All', label: 'All Courses' }])
  const [batchOptions, setBatchOptions] = useState([{ value: 'All', label: 'All Batches' }])
  const [subjectOptions, setSubjectOptions] = useState([{ value: 'All', label: 'All Subjects' }])
  const [groupOptions, setGroupOptions] = useState([{ value: 'All', label: 'All Groups' }])
  const [departmentOptions, setDepartmentOptions] = useState([{ value: 'All', label: 'All Departments' }])
  const academicYearOptions = [
    { value: 'All', label: 'All Academic Years' },
    ...generateAcademicYears().map((y) => ({ value: y, label: y })),
  ]

  // Load Dynamic Master Options
  useEffect(() => {
    let active = true

    async function loadMasters() {
      try {
        const [courses, batches, subjects, branches, companies, departments, groups] = await Promise.allSettled([
          getMasterOptionItems('courses'),
          getMasterOptionItems('batches'),
          getMasterOptionItems('subjects'),
          getMasterOptionItems('branches'),
          getMasterOptionItems('institutes'),
          getMasterOptionItems('departments'),
          listTestGroups({ archive: 0 }),
        ])

        if (!active) return

        if (courses.status === 'fulfilled' && Array.isArray(courses.value)) {
          setCourseOptions([
            { value: 'All', label: 'All Courses' },
            ...courses.value.map((c) => ({ value: c.id, label: c.name || c.label })),
          ])
        }
        if (batches.status === 'fulfilled' && Array.isArray(batches.value)) {
          setBatchOptions([
            { value: 'All', label: 'All Batches' },
            ...batches.value.map((b) => ({ value: b.id, label: b.name || b.label })),
          ])
        }
        if (subjects.status === 'fulfilled' && Array.isArray(subjects.value)) {
          setSubjectOptions([
            { value: 'All', label: 'All Subjects' },
            ...subjects.value.map((s) => ({ value: s.id, label: s.name || s.label })),
          ])
        }
        if (branches.status === 'fulfilled' && Array.isArray(branches.value)) {
          setBranchOptions([
            { value: 'All', label: 'All Branches' },
            ...branches.value.map((b) => ({ value: b.id, label: b.name || b.label })),
          ])
        }
        if (companies.status === 'fulfilled' && Array.isArray(companies.value)) {
          setCompanyOptions([
            { value: 'All', label: 'All Companies' },
            ...companies.value.map((c) => ({ value: c.id, label: c.name || c.label })),
          ])
        }
        if (departments.status === 'fulfilled' && Array.isArray(departments.value)) {
          setDepartmentOptions([
            { value: 'All', label: 'All Departments' },
            ...departments.value.map((d) => ({ value: d.id, label: d.name || d.label })),
          ])
        }
        if (groups.status === 'fulfilled' && Array.isArray(groups.value)) {
          setGroupOptions([
            { value: 'All', label: 'All Groups' },
            ...groups.value.map((g) => ({ value: g.id, label: g.name })),
          ])
        }
      } catch (err) {
        console.error('Failed to load filter options:', err)
      }
    }

    loadMasters()
    return () => {
      active = false
    }
  }, [])

  const buildQueryParams = useCallback(() => {
    const params = {
      page: currentPage,
      page_size: pageSize,
    }
    if (academicYear !== 'All') params.academic_year = academicYear
    if (company !== 'All') params.institute_id = company
    if (branch !== 'All') params.branch_id = branch
    if (course !== 'All') params.course_id = course
    if (batch !== 'All') params.batch_id = batch
    if (subject !== 'All') params.subject_id = subject
    if (group !== 'All') params.group_id = group
    if (department !== 'All' && department) params.department_id = Number(department)
    if (fromDate) params.from_date = fromDate
    if (toDate) params.to_date = toDate
    if (testNo.trim()) params.test_no_from = Number(testNo) || undefined
    if (searchText.trim()) params.search_text = searchText.trim()
    return params
  }, [
    currentPage,
    pageSize,
    academicYear,
    company,
    branch,
    course,
    batch,
    subject,
    group,
    department,
    fromDate,
    toDate,
    testNo,
    searchText,
  ])

  const loadTests = useCallback(async () => {
    setIsLoading(true)
    setError('')
    try {
      const params = buildQueryParams()
      const data = await listTests(params)
      if (data && typeof data === 'object' && Array.isArray(data.items)) {
        setItems(data.items)
        setTotalCount(data.total || data.items.length)
        setTotalPages(data.totalPages || Math.max(1, Math.ceil((data.total || 0) / pageSize)))
      } else if (Array.isArray(data)) {
        setItems(data)
        setTotalCount(data.length)
        setTotalPages(Math.max(1, Math.ceil(data.length / pageSize)))
      } else {
        setItems([])
        setTotalCount(0)
        setTotalPages(1)
      }
    } catch (err) {
      setError(err?.message || 'Failed to load tests')
      setItems([])
      setTotalCount(0)
      setTotalPages(1)
    } finally {
      setIsLoading(false)
    }
  }, [buildQueryParams, pageSize])

  useEffect(() => {
    loadTests()
  }, [loadTests])

  const toISODate = (date) => {
    const d = new Date(date)
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  }

  const handleDatePeriodChange = (preset) => {
    setDatePeriod(preset)
    if (preset === 'period') {
      setFromDate('')
      setToDate('')
      return
    }

    const now = new Date()
    const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const sunday = new Date(startOfDay)
    sunday.setDate(startOfDay.getDate() - ((startOfDay.getDay() + 6) % 7))

    const dayOffset = (days) => new Date(sunday.getFullYear(), sunday.getMonth(), sunday.getDate() + days)

    const ranges = {
      today: [startOfDay, startOfDay],
      yesterday: [dayOffset(-1), dayOffset(-1)],
      current_week: [dayOffset(0), dayOffset(6)],
      current_month: [new Date(now.getFullYear(), now.getMonth(), 1), new Date(now.getFullYear(), now.getMonth() + 1, 0)],
      current_year: [new Date(now.getFullYear(), 0, 1), new Date(now.getFullYear(), 11, 31)],
      last_week: [dayOffset(-7), dayOffset(-1)],
      last_month: [new Date(now.getFullYear(), now.getMonth() - 1, 1), new Date(now.getFullYear(), now.getMonth(), 0)],
      last_year: [new Date(now.getFullYear() - 1, 0, 1), new Date(now.getFullYear() - 1, 11, 31)],
      next_week: [dayOffset(7), dayOffset(13)],
      next_month: [new Date(now.getFullYear(), now.getMonth() + 1, 1), new Date(now.getFullYear(), now.getMonth() + 2, 0)],
      next_year: [new Date(now.getFullYear() + 1, 0, 1), new Date(now.getFullYear() + 1, 11, 31)],
    }

    const range = ranges[preset]
    if (range) {
      setFromDate(toISODate(range[0]))
      setToDate(toISODate(range[1]))
    }
  }

  const resetFilters = () => {
    setCompany('All')
    setAcademicYear('All')
    setBranch('All')
    setCourse('All')
    setGroup('All')
    setDepartment('All')
    setBatch('All')
    setSubject('All')
    setDatePeriod('period')
    setFromDate('')
    setToDate('')
    setTestNo('')
    setSearchText('')
    setCurrentPage(1)
  }

  async function handleDelete(id, name) {
    const confirmed = await appConfirm({
      title: 'Delete Test',
      message: `Are you sure you want to delete test #${name || id}? All test parameters and marks will be permanently removed.`,
      confirmText: 'Delete',
      variant: 'danger',
    })
    if (!confirmed) return
    setIsMutating(true)
    setError('')
    try {
      await deleteTest(id)
      setSuccess(`Test #${name || id} deleted successfully.`)
      loadTests()
    } catch (err) {
      setError(err?.message || 'Failed to delete test')
    } finally {
      setIsMutating(false)
    }
  }

  async function handleExport(format = 'XLSX') {
    setIsExporting(true)
    try {
      const filters = { ...buildQueryParams(), format }
      await requestTestsExport(filters)
      appToast.info(`Tests export (${format}) queued. Check background jobs indicator in top bar.`)
    } catch (err) {
      appToast.error(err?.message || 'Failed to queue export')
    } finally {
      setIsExporting(false)
    }
  }

  return {
    state: {
      rows: items,
      allRows: items,
      totalCount,
      isLoading,
      isMutating,
      isExporting,
      isReportsOpen,
      error,
      success,
      currentPage,
      totalPages,
      pageSize,
      company,
      academicYear,
      branch,
      course,
      group,
      department,
      batch,
      subject,
      datePeriod,
      fromDate,
      toDate,
      testNo,
      searchField,
      searchText,
      options: {
        companyOptions,
        branchOptions,
        courseOptions,
        groupOptions,
        departmentOptions,
        academicYearOptions,
        batchOptions,
        subjectOptions,
        datePeriodOptions: DATE_PERIOD_OPTIONS,
      },
    },
    actions: {
      setCompany,
      setAcademicYear,
      setBranch,
      setCourse,
      setGroup,
      setDepartment,
      setBatch,
      setSubject,
      handleDatePeriodChange,
      setFromDate,
      setToDate,
      setTestNo,
      setSearchField,
      setSearchText,
      setCurrentPage,
      setPageSize,
      setIsReportsOpen,
      search: loadTests,
      resetFilters,
      handleDelete,
      handleExport,
    },
  }
}
